import { AddLeaveForEmployeeModal } from "components/dashboard/AddLeaveForEmployeeModal";
import { DeleteLeaveForEmployeeModal } from "components/dashboard/DeleteLeaveForEmployeeModal";
import { LeaveRequestModal } from "components/dashboard/LeaveRequestModal";
import { ManagerApprovalDashboard } from "components/dashboard/ManagerApprovalDashboard";
import { TimesheetGrid } from "components/dashboard/TimesheetGrid";
import MYOBOneClickAuth from "components/MYOBOneClickAuth";

import MYOBLeaveBalanceTester from "components/MYOBLeaveBalanceTester";
import { AnnualLeaveReport } from "components/AnnualLeaveReport";
import { myobFirebaseService } from "services/myobFirebaseService";


import MYOBFirebaseTokenManager from "components/MYOBFirebaseTokenManager";


import FirebaseStatus from "components/FirebaseStatus";
import { useAuthContext } from "context/auth-context";
import { approveTimesheet, listenToAllLeaveRequests, listenToAllTimesheetApprovals, listenToAllUsers, updateLeaveStatus } from "firebaseService/firebaseService";
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Download, Filter, LogOut, Plus, Users, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LEAVE_TYPES, LOGO_URL } from "utils/types";
import { changePayPeriod, formatDate, getPayPeriod, getNSWHolidays } from "utils/utils";

const Dashboard = () => {
    const { signout, currentUserData } = useAuthContext();
    const navigate = useNavigate();
    const [allUsers, setAllUsers] = useState([]);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [timesheetApprovals, setTimesheetApprovals] = useState([]);
    const [currentPeriodStart, setCurrentPeriodStart] = useState(getPayPeriod(new Date()).start);
    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
    const [isAddLeaveModalOpen, setIsAddLeaveModalOpen] = useState(false);
    const [isDeleteLeaveModalOpen, setIsDeleteLeaveModalOpen] = useState(false);
    const [employeeForLeave, setEmployeeForLeave] = useState(null);
    const [holidays, setHolidays] = useState([]);
    const [myobService] = useState(myobFirebaseService);
    const [leaveBalanceData, setLeaveBalanceData] = useState({});
    
    // Debug: Log when leaveBalanceData changes
    useEffect(() => {
        console.log('🔄 leaveBalanceData state changed:', {
            size: Object.keys(leaveBalanceData).length,
            keys: Object.keys(leaveBalanceData),
            data: leaveBalanceData
        });
    }, [leaveBalanceData]);
    const [loadingLeaveBalances, setLoadingLeaveBalances] = useState(false);
    const [selectedManagerFilter, setSelectedManagerFilter] = useState('');

    useEffect(() => {
        const unsubUsers = listenToAllUsers((users) => {
            setAllUsers(users);
        });
        const unsubLeave = listenToAllLeaveRequests((requests) => {
            setLeaveRequests(requests);
        });
        const unsubApprovals = listenToAllTimesheetApprovals((approvals) => {
            setTimesheetApprovals(approvals);
        });
        return () => {
            unsubUsers();
            unsubLeave();
            unsubApprovals();
        };
    }, []);

    // Active users only (exclude deactivated) - used for display, export, and employee lists
    const activeUsers = useMemo(() => allUsers.filter(u => u.isActive !== false), [allUsers]);

    const handleExportAll = async () => {
        if (currentUserData?.role !== 'payroll') return;

        // Fetch holidays if not already loaded
        let holidaysToUse = holidays;
        if (!holidaysToUse || holidaysToUse.length === 0) {
            try {
                console.log('📅 Holidays not loaded, fetching...');
                holidaysToUse = await getNSWHolidays();
                if (holidaysToUse && holidaysToUse.length > 0) {
                    setHolidays(holidaysToUse);
                    console.log(`✅ Loaded ${holidaysToUse.length} holidays`);
                } else {
                    console.warn('⚠️ No holidays fetched, proceeding without holiday exclusion');
                    holidaysToUse = [];
                }
            } catch (error) {
                console.error('❌ Error fetching holidays:', error);
                holidaysToUse = [];
            }
        }

        const period = getPayPeriod(currentPeriodStart);
        const periodYear = period.start.getFullYear();
        const employeesToExport = allUsers.filter(u => u.role !== 'payroll' && u.isActive !== false);
        
        console.log(`📅 Export period year: ${periodYear}`);

        const leaveTypeHeaders = Object.keys(LEAVE_TYPES).map(key => `${LEAVE_TYPES[key].name} (Days)`);
        const headers = [
            "Employee Name",
            "Manager",
            "Pay Period",
            "Total Working Days",
            "Public Holidays (Days)",
            ...leaveTypeHeaders,
            "Leave Comments",
            "Timesheet Submitted",
            "Timesheet Approved"
        ];

        const csvSafe = (value) => {
            const str = String(value ?? '');
            const escaped = str.replace(/"/g, '""');
            return /[",\n]/.test(str) ? `"${escaped}"` : escaped;
        };

        let csvContent = headers.map(csvSafe).join(",") + "\n";

        let totalWorkingDaysInPeriod = 0;
        let d = new Date(period.start);
        while (d <= period.end) {
            const dayOfWeek = d.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                totalWorkingDaysInPeriod++;
            }
            d.setDate(d.getDate() + 1);
        }

        // Normalize period dates for comparison
        const periodStartNormalized = new Date(period.start);
        periodStartNormalized.setHours(0, 0, 0, 0);
        const periodEndNormalized = new Date(period.end);
        periodEndNormalized.setHours(23, 59, 59, 999);
        
        // Filter holidays to include ALL holidays that fall within the period date range
        // This ensures we catch holidays from any year that are within the period
        const relevantHolidays = holidaysToUse.filter(h => {
            if (!h.formattedDate || h.formattedDate === "Not applicable") return false;
            
            // Parse the holiday date
            const [year, month, day] = h.formattedDate.split('-').map(Number);
            const holidayDate = new Date(year, month - 1, day);
            holidayDate.setHours(0, 0, 0, 0);
            
            // Check if holiday falls within the period date range
            return holidayDate >= periodStartNormalized && holidayDate <= periodEndNormalized;
        });

        const holidayDatesInPeriod = relevantHolidays.filter(h => {
            const [year, month, day] = h.formattedDate.split('-').map(Number);
            const holidayDate = new Date(year, month - 1, day);
            holidayDate.setHours(0, 0, 0, 0);
            const dayOfWeek = holidayDate.getDay();
            return holidayDate >= periodStartNormalized && holidayDate <= periodEndNormalized && dayOfWeek !== 0 && dayOfWeek !== 6;
        });

        const publicHolidays = holidayDatesInPeriod.length;
        
        const periodStartYear = period.start.getFullYear();
        const periodEndYear = period.end.getFullYear();
        console.log(`📅 Period years: ${periodStartYear} to ${periodEndYear}`);
        console.log(`📅 Period date range: ${periodStartNormalized.toISOString().split('T')[0]} to ${periodEndNormalized.toISOString().split('T')[0]}`);
        console.log(`📅 Total holidays loaded: ${holidaysToUse.length}`);
        console.log(`📅 Relevant holidays within period: ${relevantHolidays.length}`);
        console.log(`📅 Holidays by year (all loaded):`, holidaysToUse.reduce((acc, h) => {
            if (h.formattedDate && h.formattedDate !== "Not applicable") {
                const year = h.formattedDate.split('-')[0];
                acc[year] = (acc[year] || 0) + 1;
            }
            return acc;
        }, {}));
        console.log(`📅 Relevant holidays by year:`, relevantHolidays.reduce((acc, h) => {
            const year = h.formattedDate.split('-')[0];
            acc[year] = (acc[year] || 0) + 1;
            return acc;
        }, {}));
        
        // Create holiday dates set from fetched holidays
        const holidayDatesSet = new Set(
            relevantHolidays.map(h => {
                // Parse YYYY-MM-DD string directly to avoid UTC conversion issues
                const [year, month, day] = h.formattedDate.split('-').map(Number);
                const holidayDate = new Date(year, month - 1, day); // month is 0-indexed
                holidayDate.setHours(0, 0, 0, 0);
                return holidayDate.getTime();
            })
        );
        
        // Also create a Set of holiday date strings for fallback comparison
        const holidayDateStringsSet = new Set(
            relevantHolidays.map(h => h.formattedDate)
        );
        
        // Hardcode December 25th and 26th for all years to ensure they're always excluded
        // This is a fallback in case the holiday API doesn't return these dates
        for (let year = 2020; year <= 2030; year++) {
            // December 25th (Christmas Day)
            const dec25 = new Date(year, 11, 25); // month is 0-indexed, so 11 = December
            dec25.setHours(0, 0, 0, 0);
            holidayDatesSet.add(dec25.getTime());
            holidayDateStringsSet.add(`${year}-12-25`);
            
            // December 26th (Boxing Day)
            const dec26 = new Date(year, 11, 26);
            dec26.setHours(0, 0, 0, 0);
            holidayDatesSet.add(dec26.getTime());
            holidayDateStringsSet.add(`${year}-12-26`);
        }
        
        console.log('📅 Added hardcoded holidays: Dec 25 & 26 for years 2020-2030');

        // Debug: Log holiday dates for the period
        console.log('📅 Holiday dates in Set (formatted):', Array.from(holidayDatesSet).map(timestamp => {
            const d = new Date(timestamp);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }).sort());
        console.log('📅 Holiday date strings in Set:', Array.from(holidayDateStringsSet).sort());
        console.log('📅 Holidays for 2025:', relevantHolidays.filter(h => h.formattedDate && h.formattedDate.startsWith('2025')).map(h => h.formattedDate));
        console.log('📅 Holidays for 2026:', relevantHolidays.filter(h => h.formattedDate && h.formattedDate.startsWith('2026')).map(h => h.formattedDate));

        const formattedPeriod = `${formatDate(period.start)} to ${formatDate(period.end)}`;

        employeesToExport.forEach(employee => {
            const timesheetApproval = timesheetApprovals.find(a => a.employeeId === employee.id && a.periodId === period.id);
            const employeeManager = allUsers.find(u => u.id === employee.managerId)?.name || "N/A";

            const leaveTotals = {};
            Object.keys(LEAVE_TYPES).forEach(key => leaveTotals[key] = 0);

            // Include approved leave always, and pending leave if timesheet has been submitted
            // Never include rejected leave
            const isSubmitted = timesheetApproval && (timesheetApproval.status === 'pending' || timesheetApproval.status === 'approved');
            
            // Debug logging for specific employee
            if (employee.name === 'Wal Ki Ng' || employee.name.includes('Wal') || employee.name === 'Peter Brooks') {
                console.log(`🔍 Debug export for ${employee.name}:`, {
                    employeeId: employee.id,
                    isSubmitted,
                    timesheetApproval,
                    allLeaveForEmployee: leaveRequests.filter(req => req.employeeId === employee.id),
                    period: { start: period.start, end: period.end, id: period.id },
                    holidaysCount: holidaysToUse.length,
                    holidayDatesSet: Array.from(holidayDatesSet).map(t => new Date(t).toISOString().split('T')[0])
                });
            }
            
            const leaveOverlapsPeriod = (req) => {
                const startStr = req.startDate?.includes('T') ? req.startDate.split('T')[0] : req.startDate;
                const endStr = req.endDate?.includes('T') ? req.endDate.split('T')[0] : req.endDate;
                if (!startStr || !endStr) return false;
                const [sy, sm, sd] = startStr.split('-').map(Number);
                const [ey, em, ed] = endStr.split('-').map(Number);
                const leaveStart = new Date(sy, sm - 1, sd);
                const leaveEnd = new Date(ey, em - 1, ed);
                const periodStart = new Date(period.start);
                periodStart.setHours(0, 0, 0, 0);
                const periodEnd = new Date(period.end);
                periodEnd.setHours(23, 59, 59, 999);
                return leaveStart <= periodEnd && leaveEnd >= periodStart;
            };

            const relevantLeaveRequests = leaveRequests.filter(req => {
                if (req.employeeId !== employee.id) return false;
                if (req.status === 'rejected') return false;
                // Only include leave that overlaps with current pay period (prevents old comments appearing in later months)
                if (!leaveOverlapsPeriod(req)) return false;
                if (req.status === 'approved') return true;
                if (req.status === 'pending' && isSubmitted) return true;
                return false;
            });
            
            // Debug logging for filtered leave
            if (employee.name === 'Wal Ki Ng' || employee.name.includes('Wal')) {
                console.log(`🔍 Filtered leave requests for ${employee.name}:`, relevantLeaveRequests);
            }

            // Deduplicate leave requests by date range and leave type
            const uniqueLeaveRequests = new Map();
            relevantLeaveRequests.forEach(req => {
                const key = `${req.leaveType}-${req.startDate}-${req.endDate}`;
                
                // If we haven't seen this leave type-date combination, or if this request is more recent
                if (!uniqueLeaveRequests.has(key) || 
                    (req.createdAt && uniqueLeaveRequests.get(key).createdAt && 
                     new Date(req.createdAt) > new Date(uniqueLeaveRequests.get(key).createdAt))) {
                    uniqueLeaveRequests.set(key, req);
                }
            });

            // Process only unique leave requests
            const daysCountedForPeter = []; // Track all days being counted for debugging
            Array.from(uniqueLeaveRequests.values()).forEach(req => {
                // Normalize dates to midnight to avoid timezone issues
                // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm:ss" formats
                const startDateStr = req.startDate.includes('T') ? req.startDate.split('T')[0] : req.startDate;
                const endDateStr = req.endDate.includes('T') ? req.endDate.split('T')[0] : req.endDate;
                
                // Parse dates manually to ensure local timezone (same as holiday dates)
                const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
                const [endYear, endMonth, endDay] = endDateStr.split('-').map(Number);
                let currentDate = new Date(startYear, startMonth - 1, startDay); // month is 0-indexed
                const endDate = new Date(endYear, endMonth - 1, endDay);
                currentDate.setHours(0, 0, 0, 0);
                endDate.setHours(0, 0, 0, 0);
                
                // Debug for specific employee
                if (employee.name === 'Wal Ki Ng' || employee.name.includes('Wal') || employee.name === 'Peter Brooks') {
                    console.log(`🔍 Processing leave request for ${employee.name}:`, {
                        leaveType: req.leaveType,
                        status: req.status,
                        startDate: req.startDate,
                        endDate: req.endDate,
                        parsedStart: currentDate,
                        parsedEnd: endDate
                    });
                }
                
                // Normalize period dates to midnight
                const periodStart = new Date(period.start);
                periodStart.setHours(0, 0, 0, 0);
                const periodEnd = new Date(period.end);
                periodEnd.setHours(23, 59, 59, 999); // Include the entire end date

                while (currentDate <= endDate) {
                    // Ensure date is normalized to midnight in local timezone
                    currentDate.setHours(0, 0, 0, 0);
                    
                    if (currentDate >= periodStart && currentDate <= periodEnd) {
                        const dayOfWeek = currentDate.getDay();
                        const dateTime = currentDate.getTime();
                        
                        // Format date string for debugging (YYYY-MM-DD in local timezone, not UTC)
                        const year = currentDate.getFullYear();
                        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                        const day = String(currentDate.getDate()).padStart(2, '0');
                        const dateString = `${year}-${month}-${day}`;
                        
                        // Check both timestamp and date string to ensure we catch the holiday
                        const isHolidayByTimestamp = holidayDatesSet.has(dateTime);
                        const isHolidayByString = holidayDateStringsSet.has(dateString);
                        // Also check hardcoded holidays (Dec 25 & 26 for all years)
                        const isHardcodedHoliday = (month === 12 && (day === 25 || day === 26));
                        const isHoliday = isHolidayByTimestamp || isHolidayByString || isHardcodedHoliday;
                        
                        // Debug for Peter Brooks - ALWAYS log Dec 25 & 26
                        if (employee.name === 'Peter Brooks' && (dateString === '2025-12-25' || dateString === '2025-12-26')) {
                            // Check if this exact timestamp exists in the Set
                            const exactMatch = holidayDatesSet.has(dateTime);
                            const stringMatch = holidayDateStringsSet.has(dateString);
                            
                            // Check hardcoded holidays directly
                            const isHardcodedHoliday = (dateString === '2025-12-25' || dateString === '2025-12-26');
                            
                            console.log(`🚨 Peter Brooks - ${dateString} - CRITICAL DEBUG:`, {
                                dayOfWeek,
                                isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
                                isHoliday,
                                isHolidayByTimestamp,
                                isHolidayByString,
                                isHardcodedHoliday,
                                dateTime,
                                dateString,
                                holidayDatesSetHas: exactMatch,
                                holidayDateStringsSetHas: stringMatch,
                                holidayDatesSetSize: holidayDatesSet.size,
                                holidayDateStringsSetSize: holidayDateStringsSet.size,
                                has2025Dec25: holidayDateStringsSet.has('2025-12-25'),
                                has2025Dec26: holidayDateStringsSet.has('2025-12-26'),
                                willCount: dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday,
                                willActuallyCount: dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday && !isHardcodedHoliday
                            });
                        }
                        
                        // Check if it's a weekday (Monday=1 to Friday=5) and not a public holiday
                        if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday) {
                            if (leaveTotals[req.leaveType] !== undefined) {
                                leaveTotals[req.leaveType]++;
                                
                                // Track days for Peter Brooks
                                if (employee.name === 'Peter Brooks') {
                                    daysCountedForPeter.push({
                                        date: dateString,
                                        dayOfWeek,
                                        isHoliday,
                                        isHolidayByTimestamp,
                                        isHolidayByString,
                                        leaveType: req.leaveType
                                    });
                                }
                            }
                        } else if (employee.name === 'Peter Brooks') {
                            // Log why days are NOT being counted
                            if (dayOfWeek === 0 || dayOfWeek === 6) {
                                daysCountedForPeter.push({
                                    date: dateString,
                                    dayOfWeek,
                                    reason: 'Weekend',
                                    isHoliday,
                                    isHolidayByTimestamp,
                                    isHolidayByString
                                });
                            } else if (isHoliday) {
                                daysCountedForPeter.push({
                                    date: dateString,
                                    dayOfWeek,
                                    reason: 'Public Holiday',
                                    isHoliday,
                                    isHolidayByTimestamp,
                                    isHolidayByString
                                });
                            }
                        }
                    }
                    // Move to next day
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            });

            const totalLeaveDays = Object.values(leaveTotals).reduce((sum, days) => sum + days, 0);

            // Collect comments from leave requests in this period
            const leaveComments = Array.from(uniqueLeaveRequests.values())
                .map(req => req.comments?.trim())
                .filter(Boolean)
                .join(' | ') || '';

            // Debug logging for final totals
            if (employee.name === 'Wal Ki Ng' || employee.name.includes('Wal') || employee.name === 'Peter Brooks') {
                console.log(`🔍 Final leave totals for ${employee.name}:`, {
                    leaveTotals,
                    totalLeaveDays,
                    uniqueLeaveCount: uniqueLeaveRequests.size
                });
                
                // Show all days counted for Peter Brooks
                if (employee.name === 'Peter Brooks') {
                    console.log(`📋 All days processed for Peter Brooks:`, daysCountedForPeter);
                    const countedDays = daysCountedForPeter.filter(d => !d.reason);
                    const excludedDays = daysCountedForPeter.filter(d => d.reason);
                    console.log(`✅ Days COUNTED (${countedDays.length}):`, countedDays.map(d => d.date));
                    console.log(`❌ Days EXCLUDED (${excludedDays.length}):`, excludedDays.map(d => `${d.date} (${d.reason})`));
                }
            }

            const isApproved = timesheetApproval?.status === 'approved';

            const rowData = [
                employee.name,
                employeeManager,
                formattedPeriod,
                totalWorkingDaysInPeriod - totalLeaveDays,
                publicHolidays,
                ...Object.values(leaveTotals),
                leaveComments,
                isSubmitted ? "Yes" : "No",
                isApproved ? "Yes" : "No"
            ].map(csvSafe);

            csvContent += rowData.join(",") + "\n";
        });

        const formattedFilePeriod = `${formatDate(period.start)}_to_${formatDate(period.end)}`.replace(/ /g, '_');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `timesheet_summary_${formattedFilePeriod}.csv`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const handleUpdateLeaveStatus = async (requestId, status) => {
        try { await updateLeaveStatus(requestId, status); }
        catch (error) { console.error("Error updating leave status:", error); }
    };

    const handleApproveTimesheet = async (employeeId, periodId) => {
        if (!currentUserData || currentUserData.role !== 'manager') return;
        try {
            await approveTimesheet(employeeId, periodId, currentUserData.id);
        } catch (error) { console.error("Error approving timesheet:", error); }
    };

    const handleOpenAddLeaveModal = (employee) => {
        setEmployeeForLeave(employee);
        setIsAddLeaveModalOpen(true);
    };

    const handleOpenDeleteLeaveModal = (employee) => {
        setEmployeeForLeave(employee);
        setIsDeleteLeaveModalOpen(true);
    };

    const handleUpdateEmployee = (employeeId, updateData) => {
        // Update the employee data in the allUsers state
        setAllUsers(prevUsers => 
            prevUsers.map(user => 
                user.id === employeeId 
                    ? { ...user, ...updateData }
                    : user
            )
        );
    };

    // Function to get all employees under a manager in the hierarchy
    const getAllSubordinates = (managerId, users) => {
        const subordinates = [];
        const userMap = new Map(users.map(u => [u.id, u]));
        
        const addSubordinates = (currentManagerId) => {
            users.forEach(user => {
                if (user.managerId === currentManagerId) {
                    subordinates.push(user);
                    // Recursively add subordinates of this user if they are also managers
                    if (user.role === 'manager') {
                        addSubordinates(user.id);
                    }
                }
            });
        };
        
        addSubordinates(managerId);
        return subordinates;
    };

    // Function to fetch leave balance data from MYOB
    const fetchLeaveBalanceData = async () => {
        // Everyone can fetch from MYOB API - will use shared credentials
        if (!currentUserData) return;
        
        setLoadingLeaveBalances(true);
        try {
            console.log('🔄 Fetching leave balance data from MYOB...');
            console.log('👤 Current user:', currentUserData.name, `(${currentUserData.role})`);
            const employeesWithLeave = await myobService.getAllEmployeesWithLeaveData();
            console.log('📊 MYOB data received:', employeesWithLeave?.length || 0, 'employees');
            
            if (employeesWithLeave && employeesWithLeave.length > 0) {
                const leaveDataMap = {};
                employeesWithLeave.forEach(emp => {
                    console.log(`🔍 Processing employee: ${emp.name}`, emp.leaveBalances);
                    
                    // Check if we have any leave balance data (even if it's 0)
                    if (emp.leaveBalances?.annualLeave !== null || emp.leaveBalances?.longServiceLeave !== null) {
                        // Always match against ALL users to get all leave data
                        // We'll filter what gets displayed based on role later
                        // This ensures everyone's data is fetched, but only visible to authorized users
                        
                        // Try to match by name or displayId with strict matching
                        const matchingUser = allUsers.find(user => {
                            // Exact name match (highest priority)
                            if (user.name === emp.name) {
                                console.log(`✅ Exact name match: ${emp.name} = ${user.name}`);
                                return true;
                            }
                            
                            // DisplayId match (second priority)
                            if (user.displayId && emp.displayId && user.displayId === emp.displayId) {
                                console.log(`✅ DisplayId match: ${emp.name} (${emp.displayId}) = ${user.name} (${user.displayId})`);
                                return true;
                            }
                            
                            // Normalize names for comparison (remove extra spaces, lowercase)
                            const normalizeString = (str) => str.toLowerCase().trim().replace(/\s+/g, ' ');
                            const normalizedEmpName = normalizeString(emp.name);
                            const normalizedUserName = normalizeString(user.name);
                            
                            if (normalizedEmpName === normalizedUserName) {
                                console.log(`✅ Normalized name match: "${emp.name}" = "${user.name}"`);
                                return true;
                            }
                            
                            // Only match by email if the full name is very similar (not just first name)
                            if (user.email) {
                                const emailName = user.email.split('@')[0].toLowerCase();
                                const empFirstName = emp.name.toLowerCase().split(' ')[0];
                                const empLastName = emp.name.toLowerCase().split(' ').slice(1).join(' ');
                                
                                // Check if email contains both first and last name
                                if (empLastName && emailName.includes(empFirstName) && emailName.includes(empLastName)) {
                                    console.log(`✅ Email full name match: ${emp.name} matches email ${user.email}`);
                                    return true;
                                }
                            }
                            
                            return false;
                        });
                        
                        if (matchingUser) {
                            leaveDataMap[matchingUser.id] = {
                                annualLeaveHours: emp.leaveBalances.annualLeave?.totalHours || 0,
                                longServiceLeaveHours: emp.leaveBalances.longServiceLeave?.totalHours || 0
                            };
                            console.log(`✅ Matched ${emp.name} with user ${matchingUser.name}, leave data:`, leaveDataMap[matchingUser.id]);
                        } else {
                            console.log(`❌ No matching user found for employee: ${emp.name}`);
                            console.log(`   Tried matching against:`, allUsers.map(u => ({ name: u.name, displayId: u.displayId, email: u.email })));
                        }
                    } else {
                        console.log(`⚠️ No leave balance data for employee: ${emp.name}`);
                    }
                });
                
                // Debug: Check for specific employees
                console.log('🔍 Looking for Paul Allsop in MYOB data...');
                const paulAllsop = employeesWithLeave.find(emp => emp.name.includes('Paul Allsop'));
                if (paulAllsop) {
                    console.log('✅ Found Paul Allsop in MYOB:', paulAllsop);
                } else {
                    console.log('❌ Paul Allsop not found in MYOB data');
                }
                
                console.log('🔍 Looking for Paul Addison in system users...');
                const paulAddison = allUsers.find(user => user.name.includes('Paul Addison'));
                if (paulAddison) {
                    console.log('✅ Found Paul Addison in system:', paulAddison);
                    console.log('   His leave data:', leaveDataMap[paulAddison.id]);
                } else {
                    console.log('❌ Paul Addison not found in system users');
                }
                
                // Debug: Check all users with "Paul" in their name
                console.log('🔍 All users with "Paul" in name:', allUsers.filter(user => user.name.toLowerCase().includes('paul')));
                
                // Debug: Check all MYOB employees with "Paul" in their name
                console.log('🔍 All MYOB employees with "Paul" in name:', employeesWithLeave.filter(emp => emp.name.toLowerCase().includes('paul')));
                
                // Debug: Show summary of matching results
                const matchedEmployees = Object.keys(leaveDataMap).length;
                const totalMyobEmployees = employeesWithLeave.length;
                const totalSystemUsers = allUsers.length;
                
                console.log('📊 MATCHING SUMMARY:');
                console.log(`   MYOB employees: ${totalMyobEmployees}`);
                console.log(`   System users: ${totalSystemUsers}`);
                console.log(`   Successful matches: ${matchedEmployees}`);
                console.log(`   Match rate: ${((matchedEmployees / totalMyobEmployees) * 100).toFixed(1)}%`);
                
                // Show some examples of unmatched employees
                const unmatchedEmployees = employeesWithLeave.filter(emp => {
                    const hasLeaveData = emp.leaveBalances?.annualLeave !== null || emp.leaveBalances?.longServiceLeave !== null;
                    const isMatched = allUsers.some(user => {
                        if (user.name === emp.name) return true;
                        if (user.displayId && emp.displayId && user.displayId === emp.displayId) return true;
                        const normalizeString = (str) => str.toLowerCase().trim().replace(/\s+/g, ' ');
                        const normalizedEmpName = normalizeString(emp.name);
                        const normalizedUserName = normalizeString(user.name);
                        if (normalizedEmpName === normalizedUserName) return true;
                        return false;
                    });
                    return hasLeaveData && !isMatched;
                });
                
                if (unmatchedEmployees.length > 0) {
                    console.log('❌ Sample unmatched employees:', unmatchedEmployees.slice(0, 5).map(emp => ({
                        name: emp.name,
                        displayId: emp.displayId,
                        hasLeaveData: !!(emp.leaveBalances?.annualLeave !== null || emp.leaveBalances?.longServiceLeave !== null)
                    })));
                }
                
                console.log('🔄 Setting leaveBalanceData...', {
                    previousSize: Object.keys(leaveBalanceData).length,
                    newSize: Object.keys(leaveDataMap).length,
                    newData: leaveDataMap
                });
                
                // Debug: Check if current user's data is in the map
                if (currentUserData) {
                    const currentUserLeaveData = leaveDataMap[currentUserData.id];
                    console.log(`🔍 Leave data for current user (${currentUserData.name}):`, currentUserLeaveData || 'NOT FOUND');
                }
                
                setLeaveBalanceData(leaveDataMap);
                console.log('✅ Leave balance data loaded:', leaveDataMap);
                console.log('📈 Total matches found:', Object.keys(leaveDataMap).length);
            }
        } catch (error) {
            console.error('❌ Error fetching leave balance data:', error);
        } finally {
            setLoadingLeaveBalances(false);
        }
    };

    // Fetch leave balance data when component mounts or when allUsers changes
    useEffect(() => {
        console.log('🔍 useEffect triggered - allUsers.length:', allUsers.length, 'currentUserData.role:', currentUserData?.role);
        console.log('🔍 allUsers:', allUsers.map(u => ({ name: u.name, email: u.email, role: u.role })));
        
        // Everyone can fetch from MYOB API (will use shared credentials)
        if (allUsers.length > 0 && currentUserData) {
            console.log('✅ Conditions met, fetching leave balance data from MYOB for:', currentUserData.role);
            fetchLeaveBalanceData();
        } else {
            console.log('❌ Conditions not met for fetching leave data:', {
                allUsersLength: allUsers.length,
                userRole: currentUserData?.role,
                hasCurrentUser: !!currentUserData,
                shouldFetch: allUsers.length > 0 && !!currentUserData
            });
        }
    }, [allUsers, currentUserData]);

    // Get list of managers for the filter dropdown (active users only)
    const managers = useMemo(() => {
        let availableManagers = [];
        
        if (currentUserData?.role === 'payroll') {
            // Payroll can see all active managers
            availableManagers = activeUsers.filter(u => u.role === 'manager' || u.role === 'payroll');
        } else if (currentUserData?.role === 'manager') {
            // Managers can only see themselves and their subordinate managers
            const subordinates = getAllSubordinates(currentUserData.id, activeUsers);
            availableManagers = [
                currentUserData,
                ...subordinates.filter(u => u.role === 'manager')
            ];
        } else {
            // Regular employees don't see the filter, but return empty array just in case
            availableManagers = [];
        }
        
        return availableManagers.sort((a, b) => a.name.localeCompare(b.name));
    }, [activeUsers, currentUserData]);

    // Calculate total employees count (without filter) for display - active users only
    const totalEmployeesCount = useMemo(() => {
        if (!currentUserData) return 0;
        
        if (currentUserData.role === 'payroll') {
            return activeUsers.filter(u => u.role).length;
        } else if (currentUserData.role === 'manager') {
            const subordinates = getAllSubordinates(currentUserData.id, activeUsers);
            return 1 + subordinates.length; // manager + subordinates
        } else {
            return 1; // just themselves
        }
    }, [currentUserData, activeUsers]);

    // Calculate "No Manager" count for display - active users only
    const noManagerCount = useMemo(() => {
        if (!currentUserData) return 0;
        
        let baseEmployees = [];
        if (currentUserData.role === 'payroll') {
            baseEmployees = activeUsers.filter(u => u.role);
        } else if (currentUserData.role === 'manager') {
            const subordinates = getAllSubordinates(currentUserData.id, activeUsers);
            baseEmployees = [currentUserData, ...subordinates];
        } else {
            baseEmployees = activeUsers.filter(u => u.id === currentUserData.id);
        }
        
        return baseEmployees.filter(emp => !emp.managerId).length;
    }, [currentUserData, activeUsers]);

    const displayedEmployees = useMemo(() => {
        if (!currentUserData) return [];
        
        let employees = [];
        if (currentUserData.role === 'payroll') {
            employees = activeUsers.filter(u => u.role);
        } else if (currentUserData.role === 'manager') {
            // Include the manager themselves and all their subordinates (including sub-managers)
            const subordinates = getAllSubordinates(currentUserData.id, activeUsers);
            employees = [currentUserData, ...subordinates];
        } else {
            employees = activeUsers.filter(u => u.id === currentUserData.id);
        }
        
        // Apply manager filter if selected
        if (selectedManagerFilter) {
            if (selectedManagerFilter === 'no-manager') {
                // Filter for employees with no manager
                employees = employees.filter(emp => !emp.managerId);
            } else {
                // Filter for employees under the selected manager (including the manager themselves)
                const subordinates = getAllSubordinates(selectedManagerFilter, activeUsers);
                const manager = activeUsers.find(u => u.id === selectedManagerFilter);
                employees = employees.filter(emp => 
                    emp.id === selectedManagerFilter || 
                    subordinates.some(sub => sub.id === emp.id)
                );
            }
        }
        
        console.log('🔄 displayedEmployees recalculating...', {
            leaveBalanceDataKeys: Object.keys(leaveBalanceData),
            leaveBalanceDataSize: Object.keys(leaveBalanceData).length,
            activeUsersLength: activeUsers.length,
            selectedManagerFilter: selectedManagerFilter,
            filteredCount: employees.length
        });
        
        // Merge leave balance data with employee data
        return employees.map(employee => {
            const myobData = leaveBalanceData[employee.id];
            const storedData = {
                annualLeaveHours: employee.annualLeaveHours,
                longServiceLeaveHours: employee.longServiceLeaveHours
            };
            
            const result = {
                ...employee,
                // Use MYOB data if available, otherwise show stored data, otherwise undefined
                annualLeaveHours: myobData?.annualLeaveHours ?? storedData.annualLeaveHours ?? undefined,
                longServiceLeaveHours: myobData?.longServiceLeaveHours ?? storedData.longServiceLeaveHours ?? undefined
            };
            
            // Debug: Log the first few employees to see what's happening
            if (employees.indexOf(employee) < 3) {
                console.log(`🔍 Employee ${employee.name}:`, {
                    myobData: myobData,
                    storedData: storedData,
                    result: {
                        annualLeaveHours: result.annualLeaveHours,
                        longServiceLeaveHours: result.longServiceLeaveHours
                    },
                    hasMyobData: !!myobData,
                    hasStoredData: !!(storedData.annualLeaveHours || storedData.longServiceLeaveHours)
                });
            }
            
            return result;
        });
    }, [currentUserData, activeUsers, leaveBalanceData, selectedManagerFilter]);

    const payPeriod = getPayPeriod(currentPeriodStart);
    const periodTitle = `${payPeriod.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${payPeriod.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    return (
        <div className="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8 font-sans">
            {isLeaveModalOpen && <LeaveRequestModal user={currentUserData} onClose={() => setIsLeaveModalOpen(false)} />}
            {isAddLeaveModalOpen && <AddLeaveForEmployeeModal employee={employeeForLeave} manager={currentUserData} onClose={() => setIsAddLeaveModalOpen(false)} />}
            {isDeleteLeaveModalOpen && <DeleteLeaveForEmployeeModal employee={employeeForLeave} approvedLeaves={leaveRequests.filter(r => r.employeeId === employeeForLeave.id && r.status === "approved")} onClose={() => setIsDeleteLeaveModalOpen(false)} />}

            <header className="flex flex-col md:flex-row justify-between items-center mb-6 gap-6">
                <div className="flex-shrink-0">
                    <img src={LOGO_URL} alt="Billbergia Logo" className="h-12" />
                </div>

                <div className="flex flex-col items-center">
                    <div className="flex items-center gap-2 md:gap-4">
                        <button onClick={() => setCurrentPeriodStart(p => changePayPeriod(p, -1))} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ArrowLeft size={20} /></button>
                        <h1 className="text-xl md:text-2xl font-bold text-gray-800 text-center whitespace-nowrap">{periodTitle}</h1>
                        <button onClick={() => setCurrentPeriodStart(p => changePayPeriod(p, 1))} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ArrowRight size={20} /></button>
                    </div>
                    {(currentUserData?.role === 'payroll' || currentUserData?.role === 'manager') && (
                        <div className="mt-2 flex items-center gap-2">
                            <Filter size={14} className="text-gray-600" />
                            <select
                                value={selectedManagerFilter}
                                onChange={(e) => setSelectedManagerFilter(e.target.value)}
                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Teams ({totalEmployeesCount} users)</option>
                                <option value="no-manager">No Manager ({noManagerCount} users)</option>
                                {managers.map(manager => {
                                    const teamSize = getAllSubordinates(manager.id, activeUsers).length + 1; // +1 for the manager
                                    return (
                                        <option key={manager.id} value={manager.id}>
                                            {manager.name}'s Team ({teamSize} users)
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-3 flex-wrap justify-center">
                    <button onClick={signout} className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 border"><LogOut size={16} /> Logout</button>
                    
                    {/* Manager Timesheet Status */}
                    {currentUserData.role === 'manager' && (() => {
                        const approval = timesheetApprovals.find(
                            a => a.employeeId === currentUserData.id && a.periodId === payPeriod.id
                        );
                        const isApproved = approval?.status === 'approved';
                        const isSubmitted = approval?.status === 'pending';

                        return (
                            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border">
                                <Clock size={16} className="text-gray-600" />
                                <span className="text-sm font-medium text-gray-700">
                                    Timesheet: {isApproved ? 'Approved' : isSubmitted ? 'Submitted' : 'Not submitted'}
                                </span>
                                {isApproved && <CheckCircle size={16} className="text-green-600" />}
                                {isSubmitted && <Clock size={16} className="text-blue-600" />}
                                {!isSubmitted && !isApproved && <XCircle size={16} className="text-gray-500" />}
                            </div>
                        );
                    })()}

                    {currentUserData.role === 'employee' && (() => {
                        const approval = timesheetApprovals.find(
                            a => a.employeeId === currentUserData.id && a.periodId === payPeriod.id
                        );
                        const isApproved = approval?.status === 'approved';
                        const isSubmitted = approval?.status === 'pending';

                        return (
                            <div className="flex items-center gap-3">
                                {/* Timesheet Status */}
                                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border">
                                    <Clock size={16} className="text-gray-600" />
                                    <span className="text-sm font-medium text-gray-700">
                                        Timesheet: {isApproved ? 'Approved' : isSubmitted ? 'Submitted' : 'Not submitted'}
                                    </span>
                                    {isApproved && <CheckCircle size={16} className="text-green-600" />}
                                    {isSubmitted && <Clock size={16} className="text-blue-600" />}
                                    {!isSubmitted && !isApproved && <XCircle size={16} className="text-gray-500" />}
                                </div>

                                {/* Request Leave Button */}
                                {!isApproved ? (
                                    <button
                                        onClick={() => setIsLeaveModalOpen(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700"
                                    >
                                        <Plus size={20} /> Request Leave
                                    </button>
                                ) : (
                                    <button
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md cursor-not-allowed opacity-60"
                                        disabled
                                    >
                                        <Plus size={20} /> Request Leave
                                    </button>
                                )}
                            </div>
                        );
                    })()}

                    {currentUserData.role === 'payroll' && (
                        <>
                            {/* Payroll Timesheet Status */}
                            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border">
                                <Clock size={16} className="text-gray-600" />
                                <span className="text-sm font-medium text-gray-700">
                                    Admin View - All Timesheets
                                </span>
                                <CheckCircle size={16} className="text-green-600" />
                            </div>
                            
                            <button onClick={() => navigate('/staff-management')} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700"><Users size={16} /> Manage Staff</button>
                            <button onClick={handleExportAll} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg shadow-md hover:bg-purple-700"><Download size={16} /> Export All</button>
                        </>
                    )}
                </div>
            </header >

            <main>
                {currentUserData.role === 'manager' && <ManagerApprovalDashboard requests={leaveRequests.filter(r => r.managerId === currentUserData.id)} onUpdateRequest={handleUpdateLeaveStatus} />}
                
                {/* Firebase Status Section */}
                <div className="mb-6">
                    <FirebaseStatus />
                </div>



                {/* MYOB Integration Section */}
                {currentUserData.role === 'payroll' && (
                    <div className="mb-6 space-y-6">
                        <MYOBFirebaseTokenManager />

                        <MYOBLeaveBalanceTester />
                        
                        {/* Leave Balance Data Section */}
                        <div className="bg-white rounded-lg shadow-md p-6" style={{ display: 'none' }}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-800">Leave Balance Data</h3>
                                <div className="flex items-center space-x-2">
                                    {loadingLeaveBalances && (
                                        <div className="flex items-center text-blue-600">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                            <span className="text-sm">Loading...</span>
                                        </div>
                                    )}
                                    <button
                                        onClick={fetchLeaveBalanceData}
                                        disabled={loadingLeaveBalances}
                                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                    >
                                        Refresh Leave Data
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600">
                                {Object.keys(leaveBalanceData).length > 0 
                                    ? `Loaded leave balance data for ${Object.keys(leaveBalanceData).length} employees`
                                    : 'No leave balance data loaded. Click "Refresh Leave Data" to fetch from MYOB.'
                                }
                            </p>
                        </div>
                    </div>
                )}
                
                <TimesheetGrid
                    employees={displayedEmployees}
                    leaveRequests={leaveRequests}
                    payPeriod={payPeriod}
                    currentUser={currentUserData}
                    holidays={holidays}
                    setHolidays={setHolidays}
                    timesheetApprovals={timesheetApprovals}
                    onApproveTimesheet={handleApproveTimesheet}
                    onOpenAddLeaveModal={handleOpenAddLeaveModal}
                    onOpenDeleteLeaveModal={handleOpenDeleteLeaveModal}
                    onUpdateEmployee={handleUpdateEmployee}
                    loadingLeaveBalances={loadingLeaveBalances}
                />

                {/* MYOB One-Click Authorization Section */}
                {currentUserData.role === 'payroll' && (
                    <div className="mt-6 mb-6">
                        <MYOBOneClickAuth />
                    </div>
                )}

                {/* Annual Leave Report Section */}
                {currentUserData.role === 'payroll' && (
                    <div className="mb-6">
                        <AnnualLeaveReport />
                    </div>
                )}
            </main>
            <footer className="text-center mt-8 text-gray-500 text-sm">
                <p>Logged in as: <span className="font-semibold">{currentUserData.name}</span> (<span className="capitalize">{currentUserData.role}</span>)</p>
                <div className="flex justify-center items-center flex-wrap gap-x-4 gap-y-2 mt-4">
                    <span className="font-bold">Legend:</span>
                    {Object.values(LEAVE_TYPES).map(lt => (
                        <span key={lt.name} className="flex items-center gap-1.5">
                            <span className={`w-3 h-3 rounded-sm ${lt.color}`}></span>
                            <span>{lt.abbreviation}: {lt.name}</span>
                        </span>
                    ))}
                </div>
            </footer>
        </div >
    );
}

export default Dashboard;